/**
 * deployInputSources — the GENERIC, registry-driven source→picker map for
 * the marketplace DEPLOY modal's "Required inputs" section.
 *
 * ── The contract ────────────────────────────────────────────────────
 * Each workflow template's `inputSchema` (zod → JSON Schema, surfaced on
 * the marketplace row as `entry.inputSchema`) can annotate individual
 * properties with a `deployInput` object:
 *
 *   {
 *     "deployInput": {
 *       "required":   true,           // deploy-UI gate (separate from zod-optional)
 *       "source":     "github-repo",  // selects the input control (registry key)
 *       "deriveFrom": "repoUrl",      // field is auto-resolved from another field
 *       "label":      "GitHub repo",  // field label (falls back to schema.title)
 *       "help":       "…"             // field hint (falls back to schema.description)
 *     }
 *   }
 *
 * The deploy UI renders ONE control per deploy-input field by looking up
 * `deployInput.source` in the registry below. `deriveFrom` fields are
 * NOT entered by the user — they render a read-only hint and the backend
 * resolves their value at deploy time (we send nothing for them).
 *
 * ── How a future agent / source plugs in (the whole point) ──────────
 * Adding a brand-new deploy-time input control is ONE entry in
 * SOURCE_REGISTRY below — no changes to the modal, the section, or the
 * gate. A picker component receives a uniform props bag:
 *
 *   { name, schema, deployInput, value, onChange, disabled, error,
 *     allValues }
 *
 *   - name        — the schema property key (e.g. "repoUrl")
 *   - schema      — that property's JSON Schema node
 *   - deployInput — the parsed deployInput annotation
 *   - value       — current value for THIS field (string/obj/undefined)
 *   - onChange    — (val) => void; call to update this field's value
 *   - disabled    — deploy in flight / external disable
 *   - error       — string error to render inline, or undefined
 *   - allValues   — the whole collected-input object (so a derived/
 *                   dependent picker can read its `deriveFrom` source)
 *
 * The picker returns just the CONTROL (no label / help — the section
 * renders those uniformly). Register it:
 *
 *   SOURCE_REGISTRY['my-source'] = MyPicker;
 *
 * Unknown `source` values fall back to the schema-driven primitive field
 * (so a template that ships a new source string the frontend hasn't been
 * taught yet still renders a usable text input rather than nothing).
 */

import GithubRepoPicker from './pickers/GithubRepoPicker';
import DerivedField from './pickers/DerivedField';
import PrimitiveDeployField from './pickers/PrimitiveDeployField';

/**
 * source string → picker component. Add an entry to teach the deploy UI
 * a new input control. `default` is the fallback for any field whose
 * `source` isn't in the map (or that has no `source` at all but is
 * deploy-required) — a plain schema-driven primitive field.
 */
export const SOURCE_REGISTRY = {
  'github-repo': GithubRepoPicker,
  'circleci-project': DerivedField, // resolved from repoUrl; user enters nothing
  default: PrimitiveDeployField,
};

/**
 * Resolve the picker component for a field given its deployInput
 * annotation. A field with `deriveFrom` is ALWAYS rendered by
 * DerivedField (read-only hint) regardless of its `source`, because the
 * user never enters a derived value — the backend resolves it.
 */
export function pickerForField(deployInput) {
  if (deployInput?.deriveFrom) return DerivedField;
  const source = deployInput?.source;
  return (source && SOURCE_REGISTRY[source]) || SOURCE_REGISTRY.default;
}

/**
 * Does this property declare a deploy-input the modal should render?
 * True when it carries a `deployInput` that is either required at deploy
 * OR selects a non-trivial source / is derived. (A bare `deployInput:{}`
 * with nothing meaningful is ignored — keeps backward-compat: templates
 * with no annotation render no section.)
 */
export function isDeployInputField(propSchema) {
  const di = propSchema?.deployInput;
  if (!di || typeof di !== 'object') return false;
  return Boolean(di.required || di.source || di.deriveFrom);
}

/**
 * A user-facing derived field is resolved by the backend → the user does
 * NOT enter it. We render a hint for it but never collect/validate a
 * value, and we omit it from the deploy payload.
 */
export function isDerivedField(deployInput) {
  return Boolean(deployInput?.deriveFrom);
}

/**
 * Extract the ordered list of deploy-input fields from an inputSchema.
 * Each item: { name, schema, deployInput, derived, required }. Schema
 * property declaration order is preserved (matches how the template
 * author wrote them).
 */
export function collectDeployInputFields(inputSchema) {
  const props = inputSchema?.properties;
  if (!props || typeof props !== 'object') return [];
  const out = [];
  for (const name of Object.keys(props)) {
    const schema = props[name];
    if (!isDeployInputField(schema)) continue;
    const deployInput = schema.deployInput || {};
    out.push({
      name,
      schema,
      deployInput,
      derived: isDerivedField(deployInput),
      // A field is a deploy gate when deployInput.required is true AND it
      // isn't a derived field (derived ones the backend fills, so they
      // never block the user's click).
      required: Boolean(deployInput.required) && !isDerivedField(deployInput),
    });
  }
  return out;
}

/**
 * Pre-flight validation of the collected deploy inputs against the
 * fields' `required` gate. Returns { ok, errors } where errors maps
 * field name → message. Only checks "required & empty" — the backend is
 * the authoritative validator (URL shape, repo existence, CircleCI
 * resolvability, …). Derived fields are skipped (backend-resolved).
 */
export function validateDeployInputs(fields, values) {
  const errors = {};
  for (const f of fields) {
    if (f.derived) continue;
    if (!f.required) continue;
    const v = values?.[f.name];
    const empty = v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
    if (empty) errors[f.name] = 'Required';
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/**
 * Build the `defaultInput` object to send on deploy. Derived fields are
 * OMITTED entirely (the backend resolves them from their `deriveFrom`
 * source). Empty values are dropped so the backend's own defaults /
 * env-fallbacks can kick in. Returns undefined when nothing meaningful
 * was entered (so the deploy body stays byte-identical to today for
 * templates with no deploy inputs).
 */
export function buildDefaultInput(fields, values) {
  const out = {};
  for (const f of fields) {
    if (f.derived) continue; // backend resolves; never send a value
    const v = values?.[f.name];
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    out[f.name] = typeof v === 'string' ? v.trim() : v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
